import { ChakraV3, stringParam, useQueryParams } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaRegCheckCircle } from 'react-icons/fa';
import { Navigate, useNavigate } from 'react-router-dom';
import { HookedFormField } from '../../components/forms/HookedFormField';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { useAuth } from '../../context/AuthContext';
import { asPiccoloError } from '../../utils/errors';
import { AuthCard } from './components/AuthCard';
import { AuthLayout } from './components/AuthLayout';

type FormValues = {
  oobCode: string;
};

export const MFAUnenrol = () => {
  const navigate = useNavigate();
  const { unenrolMfa, logout } = useAuth();

  const [params] = useQueryParams({
    oobCode: stringParam,
  });

  const { oobCode } = params;

  const methods = useForm<FormValues>({
    defaultValues: {
      oobCode,
    },
  });

  const onClickLogin = () => {
    void navigate('/login');
  };

  const onSubmit = async (data: FormValues) => {
    if (!oobCode) {
      return;
    }

    try {
      await unenrolMfa(oobCode);
      await logout();
    } catch (error) {
      const piccoloError = asPiccoloError(error);
      methods.setError('oobCode', { message: piccoloError.message });
    }
  };

  if (!oobCode) {
    return <Navigate to='/login' />;
  }

  const content = P.run(() => {
    if (methods.formState.isSubmitSuccessful) {
      return (
        <AuthCard>
          <ChakraV3.Stack gap={4} align='center'>
            <ChakraV3.Icon color='success' boxSize={16}>
              <FaRegCheckCircle />
            </ChakraV3.Icon>
            <ChakraV3.Text fontSize='sm' textAlign='center'>
              You have successfully removed 2-Step Verification from your account.
            </ChakraV3.Text>
            <ChakraV3.Button
              fontWeight='semibold'
              fontSize='md'
              size='md'
              w='full'
              colorPalette='brand'
              onClick={onClickLogin}
            >
              Login
            </ChakraV3.Button>
          </ChakraV3.Stack>
        </AuthCard>
      );
    }

    return (
      <AuthCard>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Text fontWeight='semibold' fontSize='2xl'>
            Remove 2-Step Verification
          </ChakraV3.Text>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            Are you sure you want to remove 2-Step Verification from your account? This will make
            your account less secure.
          </ChakraV3.Text>
        </ChakraV3.Stack>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Stack gap={0}>
              <HookedFormField name='oobCode' />
              <HookedSubmitButton
                fontSize='md'
                fontWeight='semibold'
                size='md'
                mt={4}
                loadingText='Removing MFA...'
                colorPalette='red'
                disabled={false}
              >
                Confirm removal
              </HookedSubmitButton>
            </ChakraV3.Stack>
          </form>
        </FormProvider>
      </AuthCard>
    );
  });

  return <AuthLayout>{content}</AuthLayout>;
};
